import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { AlertTriangle, Edit, GripVertical, Plus, Save, Trash2, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import httpClient, { ApiResponse } from '../../api/httpClient';
import { API_ENDPOINTS } from '@/api/endpoints';
import { usePermissions } from '@/hooks/usePermissions';
import { CustomAttributeDefinition, CustomAttributeFieldType } from '@/models/customAttribute';
import { ModulePermission, PermissionAction } from '@/types/permissions.ts';

interface CustomAttributeDefinitionsManagerProps {}

export const CustomAttributeDefinitionsManager: React.FC<
    CustomAttributeDefinitionsManagerProps
> = () => {
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const { hasPermission } = usePermissions();

    const [isCreating, setIsCreating] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [formData, setFormData] = useState<Partial<CustomAttributeDefinition>>({});
    const [newOption, setNewOption] = useState('');

    // Check permissions
    const canView = hasPermission(ModulePermission.SYSTEM_SETTINGS, PermissionAction.VIEW);
    const canEdit = hasPermission(ModulePermission.SYSTEM_SETTINGS, PermissionAction.EDIT);

    // Fetch custom attribute definitions
    const { data: definitions, isLoading } = useQuery<ApiResponse<CustomAttributeDefinition[]>>({
        queryKey: ['customAttributeDefinitions'],
        queryFn: async () => {
            const { data } = await httpClient.get<ApiResponse<CustomAttributeDefinition[]>>(
                API_ENDPOINTS.SYSTEM.CUSTOM_ATTRIBUTE_DEFINITIONS
            );
            return data;
        },
        enabled: canView,
    });

    // Create definition mutation
    const createDefinitionMutation = useMutation({
        mutationFn: async (data: Partial<CustomAttributeDefinition>) => {
            const { data: response } = await httpClient.post<
                ApiResponse<CustomAttributeDefinition>
            >(API_ENDPOINTS.SYSTEM.CUSTOM_ATTRIBUTE_DEFINITIONS, data);
            return response;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['customAttributeDefinitions'] });
            setIsCreating(false);
            setFormData({});
            enqueueSnackbar('Custom attribute definition created successfully', {
                variant: 'success',
            });
        },
        onError: (error: any) => {
            enqueueSnackbar(
                error.response?.data?.message || 'Failed to create custom attribute definition',
                { variant: 'error' }
            );
        },
    });

    // Update definition mutation
    const updateDefinitionMutation = useMutation({
        mutationFn: async ({
            id,
            data,
        }: {
            id: string;
            data: Partial<CustomAttributeDefinition>;
        }) => {
            const { data: response } = await httpClient.put<ApiResponse<CustomAttributeDefinition>>(
                `${API_ENDPOINTS.SYSTEM.CUSTOM_ATTRIBUTE_DEFINITIONS}/${id}`,
                data
            );
            return response;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['customAttributeDefinitions'] });
            setEditingId(null);
            setFormData({});
            enqueueSnackbar('Custom attribute definition updated successfully', {
                variant: 'success',
            });
        },
        onError: (error: any) => {
            enqueueSnackbar(
                error.response?.data?.message || 'Failed to update custom attribute definition',
                { variant: 'error' }
            );
        },
    });

    // Delete definition mutation
    const deleteDefinitionMutation = useMutation({
        mutationFn: async (id: string) => {
            await httpClient.delete(`${API_ENDPOINTS.SYSTEM.CUSTOM_ATTRIBUTE_DEFINITIONS}/${id}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['customAttributeDefinitions'] });
            enqueueSnackbar('Custom attribute definition deleted successfully', {
                variant: 'success',
            });
        },
        onError: (error: any) => {
            enqueueSnackbar(
                error.response?.data?.message || 'Failed to delete custom attribute definition',
                { variant: 'error' }
            );
        },
    });

    const handleCreate = () => {
        if (!canEdit) return;

        setIsCreating(true);
        setFormData({
            name: '',
            label: '',
            field_type: 'text',
            is_required: false,
            validation_rules: {},
            options: [],
            sort_order: (definitions?.data?.length || 0) + 1,
            is_active: true,
        });
    };

    const handleEdit = (definition: CustomAttributeDefinition) => {
        if (!canEdit) return;

        setEditingId(definition.id);
        setFormData({
            ...definition,
            options: definition.options || [],
        });
    };

    const handleSave = () => {
        if (!canEdit) return;

        if (isCreating) {
            createDefinitionMutation.mutate(formData);
        } else if (editingId) {
            updateDefinitionMutation.mutate({ id: editingId, data: formData });
        }
    };

    const handleCancel = () => {
        setIsCreating(false);
        setEditingId(null);
        setFormData({});
        setNewOption('');
    };

    const handleDelete = (id: string) => {
        if (!canEdit) return;

        if (
            window.confirm(
                'Are you sure you want to delete this custom attribute definition? This action cannot be undone.'
            )
        ) {
            deleteDefinitionMutation.mutate(id);
        }
    };

    const addOption = () => {
        if (newOption.trim() && formData.options) {
            setFormData({
                ...formData,
                options: [...formData.options, newOption.trim()],
            });
            setNewOption('');
        }
    };

    const removeOption = (index: number) => {
        if (formData.options) {
            setFormData({
                ...formData,
                options: formData.options.filter((_, i) => i !== index),
            });
        }
    };

    const fieldTypes: { value: CustomAttributeFieldType; label: string; description: string }[] = [
        { value: 'text', label: 'Text', description: 'Single line text input' },
        { value: 'number', label: 'Number', description: 'Numeric input with validation' },
        { value: 'date', label: 'Date', description: 'Date picker input' },
        { value: 'boolean', label: 'Boolean', description: 'Yes/No toggle switch' },
        { value: 'select', label: 'Select', description: 'Single choice dropdown' },
        { value: 'multiselect', label: 'Multi-Select', description: 'Multiple choice selection' },
    ];

    if (!canView) {
        return (
            <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                    You don't have permission to view custom attribute definitions.
                </AlertDescription>
            </Alert>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h3 className="text-lg font-semibold">Custom Attribute Definitions</h3>
                    <p className="text-sm text-muted-foreground">
                        Manage custom fields for Leads and Customers
                    </p>
                </div>
                {canEdit && (
                    <Button onClick={handleCreate} disabled={isCreating || editingId !== null}>
                        <Plus className="mr-2 h-4 w-4" />
                        Add Custom Attribute
                    </Button>
                )}
            </div>

            {/* Create/Edit Form */}
            {(isCreating || editingId) && (
                <Card>
                    <CardHeader>
                        <CardTitle>
                            {isCreating ? 'Create Custom Attribute' : 'Edit Custom Attribute'}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="name">Field Name</Label>
                                <Input
                                    id="name"
                                    value={formData.name || ''}
                                    onChange={e =>
                                        setFormData({ ...formData, name: e.target.value })
                                    }
                                    placeholder="e.g., industry, priority"
                                    disabled={editingId !== null} // Can't change name when editing
                                />
                                <p className="text-xs text-muted-foreground">
                                    Internal field name (lowercase, no spaces)
                                </p>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="label">Display Label</Label>
                                <Input
                                    id="label"
                                    value={formData.label || ''}
                                    onChange={e =>
                                        setFormData({ ...formData, label: e.target.value })
                                    }
                                    placeholder="e.g., Industry, Priority Level"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="field_type">Field Type</Label>
                                <Select
                                    value={formData.field_type || 'text'}
                                    onValueChange={(value: CustomAttributeFieldType) =>
                                        setFormData({ ...formData, field_type: value })
                                    }
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {fieldTypes.map(type => (
                                            <SelectItem key={type.value} value={type.value}>
                                                <div>
                                                    <div className="font-medium">{type.label}</div>
                                                    <div className="text-xs text-muted-foreground">
                                                        {type.description}
                                                    </div>
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="sort_order">Sort Order</Label>
                                <Input
                                    id="sort_order"
                                    type="number"
                                    value={formData.sort_order || 0}
                                    onChange={e =>
                                        setFormData({
                                            ...formData,
                                            sort_order: parseInt(e.target.value) || 0,
                                        })
                                    }
                                />
                            </div>
                        </div>

                        {/* Options for select/multiselect fields */}
                        {(formData.field_type === 'select' ||
                            formData.field_type === 'multiselect') && (
                            <div className="space-y-2">
                                <Label>Options</Label>
                                <div className="space-y-2">
                                    {formData.options?.map((option, index) => (
                                        <div key={index} className="flex items-center gap-2">
                                            <Input value={option} readOnly />
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => removeOption(index)}
                                            >
                                                <X className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    ))}
                                    <div className="flex items-center gap-2">
                                        <Input
                                            value={newOption}
                                            onChange={e => setNewOption(e.target.value)}
                                            placeholder="Add new option"
                                            onKeyPress={e => e.key === 'Enter' && addOption()}
                                        />
                                        <Button variant="outline" size="sm" onClick={addOption}>
                                            <Plus className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-2">
                                <Switch
                                    id="is_required"
                                    checked={formData.is_required || false}
                                    onCheckedChange={checked =>
                                        setFormData({ ...formData, is_required: checked })
                                    }
                                />
                                <Label htmlFor="is_required">Required Field</Label>
                            </div>

                            <div className="flex items-center space-x-2">
                                <Switch
                                    id="is_active"
                                    checked={formData.is_active !== false}
                                    onCheckedChange={checked =>
                                        setFormData({ ...formData, is_active: checked })
                                    }
                                />
                                <Label htmlFor="is_active">Active</Label>
                            </div>
                        </div>

                        <div className="flex justify-end space-x-2">
                            <Button variant="outline" onClick={handleCancel}>
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSave}
                                disabled={
                                    !formData.name ||
                                    !formData.label ||
                                    createDefinitionMutation.isPending ||
                                    updateDefinitionMutation.isPending
                                }
                            >
                                <Save className="mr-2 h-4 w-4" />
                                {isCreating ? 'Create' : 'Update'}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Definitions List */}
            <div className="space-y-4">
                {isLoading ? (
                    <div className="text-center py-8">Loading custom attribute definitions...</div>
                ) : definitions?.data?.length === 0 ? (
                    <Card>
                        <CardContent className="text-center py-8">
                            <p className="text-muted-foreground">
                                No custom attribute definitions found. Create your first one to get
                                started.
                            </p>
                        </CardContent>
                    </Card>
                ) : (
                    definitions?.data?.map((definition, index) => (
                        <Card key={definition.id}>
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-4">
                                        <div className="flex items-center space-x-2">
                                            <GripVertical className="h-4 w-4 text-muted-foreground" />
                                            <Badge variant="outline">{index + 1}</Badge>
                                        </div>
                                        <div>
                                            <div className="flex items-center space-x-2">
                                                <h4 className="font-medium">{definition.label}</h4>
                                                <Badge variant="secondary">
                                                    {definition.field_type}
                                                </Badge>
                                                {definition.is_required && (
                                                    <Badge variant="destructive">Required</Badge>
                                                )}
                                                {!definition.is_active && (
                                                    <Badge variant="outline">Inactive</Badge>
                                                )}
                                            </div>
                                            <p className="text-sm text-muted-foreground">
                                                Field name: {definition.name}
                                            </p>
                                            {definition.options &&
                                                definition.options.length > 0 && (
                                                    <p className="text-xs text-muted-foreground">
                                                        Options: {definition.options.join(', ')}
                                                    </p>
                                                )}
                                        </div>
                                    </div>
                                    {canEdit && (
                                        <div className="flex items-center space-x-2">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handleEdit(definition)}
                                                disabled={isCreating || editingId !== null}
                                            >
                                                <Edit className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handleDelete(definition.id)}
                                                disabled={isCreating || editingId !== null}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    ))
                )}
            </div>
        </div>
    );
};

export default CustomAttributeDefinitionsManager;
